/**
 * SubstrateBatchTree Component
 *
 * Non-contiguous tree view showing substrate batches as the trunk,
 * with fork lines branching into strain-specific cultivation batches
 * at the inoculation point. Orthogonal lines connect spawn batches
 * to their strain branches.
 *
 * Used when display mode is "Non-contiguous" in the BatchProcessView.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { ChevronDown, ChevronRight, Package, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supabase } from '@/supabase-client'
import ProcessBar from './ProcessBar'

// ─── Colours ───────────────────────────────────────────────────────────────

const SUBSTRATE_COLOR = '#10b981' // emerald (block production)
const SPAWN_COLOR = '#f59e0b'    // amber
const UNALLOCATED_COLOR = '#d1d5db' // gray-300

// ─── Data fetching ─────────────────────────────────────────────────────────

// Phase colours matching useTimeline.js PHASE_COLORS
const CULTIVATION_PHASE_COLORS = {
  'Inoculation': '#10b981',
  'Incubation': '#60a5fa',
  'Primordia': '#60a5fa',
  'Flush 1': '#22c55e',
  'Rest': '#93c5fd',
  'Flush 2': '#16a34a',
  'Flush 3': '#15803d',
  'Spent Compost': '#6b7280',
}

function useCultivationProcesses() {
  const [processes, setProcesses] = useState({}) // keyed by strain_id
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch cultivation processes (keyed by strain_family_id)
      const [procResult, strainResult] = await Promise.all([
        supabase
          .from('production_processes')
          .select('id, name, strain_family_id, process_phases(name, sequence_order, typical_duration_days)')
          .eq('process_type', 'cultivation')
          .eq('active', true),
        supabase
          .from('strains')
          .select('id, family_id')
      ])

      if (procResult.error) throw procResult.error
      if (strainResult.error) throw strainResult.error

      // Build family_id → phases map
      const familyMap = {}
      for (const proc of (procResult.data || [])) {
        if (proc.strain_family_id) {
          familyMap[proc.strain_family_id] = (proc.process_phases || [])
            .sort((a, b) => a.sequence_order - b.sequence_order)
            .filter(p => (p.typical_duration_days || 0) > 0)
            .map(p => ({
              phaseId: null,
              name: p.name,
              label: p.name,
              sequenceOrder: p.sequence_order,
              duration: p.typical_duration_days,
              status: 'pending',
              color: CULTIVATION_PHASE_COLORS[p.name] || '#3b82f6',
              textColor: p.name === 'Rest' ? '#1e40af' : '#ffffff',
              processType: 'cultivation',
            }))
        }
      }

      // Map strain_id → phases via family_id
      const strainMap = {}
      for (const strain of (strainResult.data || [])) {
        if (strain.family_id && familyMap[strain.family_id]) {
          strainMap[strain.id] = familyMap[strain.family_id]
        }
      }

      setProcesses(strainMap)
    } catch (err) {
      console.error('[SubstrateBatchTree] Error fetching cultivation processes:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetch() }, [fetch])
  return { processes, loading }
}

function useSubstrateBatches() {
  const [substrateBatches, setSubstrateBatches] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('substrate_batches')
        .select('id, batch_code, total_blocks, created_at, status, mixed_at, cooled_at, heat_cycle_id')
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      setSubstrateBatches(data || [])
    } catch (err) {
      console.error('[SubstrateBatchTree] Error fetching substrate batches:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetch() }, [fetch])
  return { substrateBatches, loading, refetch: fetch }
}

// ─── Status badge ──────────────────────────────────────────────────────────

function SubstrateStatusBadge({ status }) {
  const config = {
    MIXING: { label: 'Mixing', className: 'bg-orange-100 text-orange-800' },
    HEAT_CYCLING: { label: 'Heat Cycling', className: 'bg-red-100 text-red-800' },
    COOLING: { label: 'Cooling', className: 'bg-sky-100 text-sky-800' },
    BAGGED: { label: 'Bagged', className: 'bg-amber-100 text-amber-800' },
    READY: { label: 'Ready', className: 'bg-emerald-100 text-emerald-800' },
    DEPLETED: { label: 'Depleted', className: 'bg-gray-100 text-gray-700' },
  }
  const cfg = config[status] || { label: status || 'Unknown', className: 'bg-slate-100 text-slate-700' }
  return (
    <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-medium', cfg.className)}>
      {cfg.label}
    </span>
  )
}

// ─── Fork connector lines (SVG) ───────────────────────────────────────────

function ForkLines({ branchCount, rowHeight, trunkWidth }) {
  if (branchCount === 0) return null

  const totalHeight = branchCount * rowHeight
  const startX = 0
  const endX = 32
  const midX = 16

  return (
    <svg
      width={endX}
      height={totalHeight}
      className="shrink-0"
      style={{ minWidth: endX }}
    >
      {/* Vertical trunk line */}
      <line
        x1={midX} y1={rowHeight / 2}
        x2={midX} y2={totalHeight - rowHeight / 2}
        stroke="#d1d5db" strokeWidth={1.5}
      />
      {/* Horizontal branch lines */}
      {Array.from({ length: branchCount }).map((_, i) => {
        const y = (i * rowHeight) + rowHeight / 2
        return (
          <line
            key={i}
            x1={midX} y1={y}
            x2={endX} y2={y}
            stroke="#d1d5db" strokeWidth={1.5}
          />
        )
      })}
      {/* Branch dots */}
      {Array.from({ length: branchCount }).map((_, i) => {
        const y = (i * rowHeight) + rowHeight / 2
        return (
          <circle
            key={`dot-${i}`}
            cx={endX} cy={y} r={3}
            fill="#9ca3af"
          />
        )
      })}
    </svg>
  )
}

// ─── Spawn connection line (orthogonal) ────────────────────────────────────

function SpawnLineage({ spawnCode }) {
  if (!spawnCode) return null
  return (
    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
      <div className="w-4 border-t border-dashed border-amber-400" />
      <span className="px-1 py-0.5 rounded bg-amber-50 text-amber-700 font-medium whitespace-nowrap">
        {spawnCode}
      </span>
    </div>
  )
}

// ─── Single strain branch row ──────────────────────────────────────────────

function StrainBranch({ batch, rowHeight, fallbackPhases }) {
  const strainName = typeof batch.strain === 'object' ? batch.strain?.name : batch.strain || 'Unknown'
  const strainCode = typeof batch.strain === 'object' ? batch.strain?.code : ''
  const blocksAllocated = batch._blocksAllocated || '?'
  const spawnCode = batch.spawnLineage?.[0]?.spawnBatch?.code || batch._spawnCode || null

  // Use actual batch phases if available, otherwise fall back to process template
  const phases = (batch.phases && batch.phases.length > 0)
    ? batch.phases
    : fallbackPhases || []

  return (
    <div className="flex items-center gap-2" style={{ height: rowHeight }}>
      {/* Block count + strain label */}
      <div className="shrink-0 w-32 flex items-center gap-2">
        <span className="text-xs font-semibold text-foreground">
          {blocksAllocated}×
        </span>
        <div className="min-w-0">
          <div className="text-xs font-medium text-foreground truncate">{strainCode || strainName}</div>
          {strainCode && <div className="text-[10px] text-muted-foreground truncate">{strainName}</div>}
        </div>
      </div>

      {/* Spawn connection */}
      {spawnCode && <SpawnLineage spawnCode={spawnCode} />}

      {/* Cultivation process bar */}
      <div className="flex-1 min-w-0">
        <ProcessBar
          phases={phases}
          mode="contiguous"
          height={28}
        />
      </div>
    </div>
  )
}

// ─── Substrate process bar (multi-phase trunk) ────────────────────────────

const SUBSTRATE_BASE_COLOR = '#10b981' // emerald — matches block process type
const SUBSTRATE_PHASES = [
  { name: 'Mixing', key: 'MIXING', shade: 1.0 },
  { name: 'Heat Cycle', key: 'HEAT_CYCLING', shade: 0.92 },
  { name: 'Cooling', key: 'COOLING', shade: 0.84 },
  { name: 'Bagging', key: 'BAGGED', shade: 0.76 },
]

// Map status to completion progress
const STATUS_PROGRESS = {
  MIXING: 0,
  HEAT_CYCLING: 1,
  COOLING: 2,
  BAGGED: 3,
  READY: 4,
  DEPLETED: 4,
}

function SubstrateProcessBar({ substrateBatch }) {
  const progress = STATUS_PROGRESS[substrateBatch.status] ?? 0

  return (
    <div className="flex rounded-lg overflow-hidden border border-border/50 bg-muted/20" style={{ height: 32 }}>
      {SUBSTRATE_PHASES.map((phase, i) => {
        const isComplete = i < progress
        const isActive = i === progress && substrateBatch.status !== 'READY' && substrateBatch.status !== 'DEPLETED'
        const statusOpacity = isComplete ? 1.0 : isActive ? 0.6 : 0.25

        return (
          <div
            key={phase.key}
            className="flex-1 flex items-center justify-center text-[10px] font-medium overflow-hidden border-r border-white/20 last:border-r-0"
            style={{
              backgroundColor: SUBSTRATE_BASE_COLOR,
              opacity: statusOpacity * phase.shade,
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            }}
          >
            {phase.name}
          </div>
        )
      })}
    </div>
  )
}

// ─── Substrate batch tree row ──────────────────────────────────────────────

function SubstrateBatchRow({ substrateBatch, productionBatches, cultivationProcesses }) {
  const [expanded, setExpanded] = useState(true)

  // Find production batches that used this substrate batch
  const linkedBatches = useMemo(() => {
    return productionBatches
      .filter(pb => {
        const subs = pb.substrateLineage || []
        return subs.some(s =>
          s.substrateBatch?.id === substrateBatch.id ||
          s.substrateBatch?.code === substrateBatch.batch_code
        )
      })
      .map(pb => {
        const subLink = (pb.substrateLineage || []).find(
          s => s.substrateBatch?.id === substrateBatch.id ||
               s.substrateBatch?.code === substrateBatch.batch_code
        )
        const spawnLink = (pb.spawnLineage || [])[0]
        return {
          ...pb,
          _blocksAllocated: subLink?.blocksAllocated || '?',
          _spawnCode: spawnLink?.spawnBatch?.code || null,
        }
      })
      .sort((a, b) => {
        // Sort by strain name
        const nameA = typeof a.strain === 'object' ? a.strain?.name : a.strain || ''
        const nameB = typeof b.strain === 'object' ? b.strain?.name : b.strain || ''
        return nameA.localeCompare(nameB)
      })
  }, [productionBatches, substrateBatch])

  const allocatedBlocks = linkedBatches.reduce((sum, b) => sum + (b._blocksAllocated || 0), 0)
  const unallocated = Math.max(0, (substrateBatch.total_blocks || 0) - allocatedBlocks)
  const branchCount = linkedBatches.length + (unallocated > 0 ? 1 : 0)
  const rowHeight = 56
  const createdDate = new Date(substrateBatch.created_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short'
  })

  return (
    <div className="mb-6">
      {/* Substrate batch header */}
      <button
        className="flex items-center gap-2 py-2 px-1 text-sm font-semibold text-foreground hover:text-primary transition-colors w-full text-left"
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Package className="w-4 h-4 text-emerald-600" />
        {substrateBatch.batch_code}
        <span className="text-xs font-normal text-muted-foreground">
          {substrateBatch.total_blocks} blocks · {createdDate}
        </span>
        <SubstrateStatusBadge status={substrateBatch.status} />
        {linkedBatches.length > 0 && (
          <span className="text-xs font-normal text-muted-foreground">
            → {linkedBatches.length} strain{linkedBatches.length !== 1 ? 's' : ''}
          </span>
        )}
      </button>

      {expanded && (
        <div className="ml-6 flex items-start">
          {/* Substrate production bar (trunk) — multi-phase */}
          <div className="shrink-0 flex flex-col justify-center" style={{ width: 220, minHeight: branchCount * rowHeight }}>
            <SubstrateProcessBar substrateBatch={substrateBatch} />
            <div className="text-center text-[10px] text-muted-foreground mt-1">
              {substrateBatch.total_blocks} blocks
            </div>
          </div>

          {/* Fork lines */}
          <ForkLines
            branchCount={branchCount}
            rowHeight={rowHeight}
            trunkWidth={180}
          />

          {/* Strain branches */}
          <div className="flex-1 min-w-0">
            {linkedBatches.map(batch => {
              const strainId = typeof batch.strain === 'object' ? batch.strain?.id : null
              return (
                <StrainBranch
                  key={batch.id || batch.code}
                  batch={batch}
                  rowHeight={rowHeight}
                  fallbackPhases={strainId ? cultivationProcesses[strainId] : null}
                />
              )
            })}

            {/* Unallocated row */}
            {unallocated > 0 && (
              <div className="flex items-center gap-2" style={{ height: rowHeight }}>
                <div className="shrink-0 w-32 flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {unallocated}×
                  </span>
                  <span className="text-xs text-muted-foreground italic">unallocated</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-xs text-muted-foreground"
                    style={{ height: 28 }}
                  >
                    Awaiting inoculation
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Orphan message when no linked batches */}
      {expanded && linkedBatches.length === 0 && unallocated === 0 && (
        <div className="ml-6 py-4 text-sm text-muted-foreground">
          No production batches linked to this substrate batch.
        </div>
      )}
    </div>
  )
}

// ─── Main component ────────────────────────────────────────────────────────

export default function SubstrateBatchTree({ batches }) {
  const { substrateBatches, loading } = useSubstrateBatches()
  const { processes: cultivationProcesses, loading: processesLoading } = useCultivationProcesses()

  // Find batches with substrate lineage (for linking)
  const batchesWithLineage = useMemo(() => {
    return batches.filter(b =>
      b.substrateLineage && b.substrateLineage.length > 0
    )
  }, [batches])

  // Find batches without substrate lineage (orphans — show separately)
  const orphanBatches = useMemo(() => {
    return batches.filter(b =>
      !b.substrateLineage || b.substrateLineage.length === 0
    )
  }, [batches])

  if (loading || processesLoading) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading substrate batches...
      </div>
    )
  }

  // Only show substrate batches that have linked production batches
  const relevantSubstrates = substrateBatches.filter(sb =>
    batchesWithLineage.some(pb =>
      (pb.substrateLineage || []).some(s =>
        s.substrateBatch?.id === sb.id || s.substrateBatch?.code === sb.batch_code
      )
    )
  )

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="text-xs text-muted-foreground mb-4">
        {relevantSubstrates.length} substrate batch{relevantSubstrates.length !== 1 ? 'es' : ''} · {batchesWithLineage.length} linked production batch{batchesWithLineage.length !== 1 ? 'es' : ''}
        {orphanBatches.length > 0 && ` · ${orphanBatches.length} unlinked`}
      </div>

      {relevantSubstrates.map(sb => (
        <SubstrateBatchRow
          key={sb.id}
          substrateBatch={sb}
          productionBatches={batchesWithLineage}
          cultivationProcesses={cultivationProcesses}
        />
      ))}

      {relevantSubstrates.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p>No substrate batches with linked production batches found.</p>
          <p className="text-xs mt-1">Production batches need substrate lineage data to appear in this view.</p>
        </div>
      )}
    </div>
  )
}
